import { Ref, ref } from "@vue/composition-api";
import { DataSourceType, ScanSession, SourceConnection } from "./model";
import * as api from "./datasources.api";
import { state } from "./state";
import { UseLoading } from "@/common/composition/loading";
import { UseModal } from "@/components/root/modal";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import StartSessionModal from "../modals/StartSessionModal.vue";
import { delayBy } from "@/fp";
import WalkthroughModalVue from "@/components/modals/walkthrough-modal/WalkthroughModal.vue";

export const UseCurrentSource = (source_id: DataSourceType) => {
  const get_source = () =>
    state.all_sources.value.find((d) => d.id === source_id)!;

  const connections: Ref<SourceConnection[]> = ref([]);

  const add_connection = async (cb: (loading: boolean) => void) => {
    await state.setup_data_source(
      get_source(),
      connections.value.map((s) => s.name),
      cb
    );
    // add the new connection
  };

  const refresh_connection_list = async () => {
    set_is_loading_data(true);
    const connections_res = await api.get_source_connections(source_id);
    if (connections_res.success) {
      connections.value = connections_res.value;
      connections.value = connections.value.map((c) => {
        const con_sessions = sessions.value.filter(
          (s) => s.connectionId == c.id
        );
        const active_sessions_count = con_sessions.filter(
          (s) => s.status == "Opened"
        ).length;
        // @ts-ignore
        c.sessions = {
          active: active_sessions_count,
          closed: con_sessions.length - active_sessions_count,
        };
        return c;
      });
    }
    set_is_loading_data(false);
  };

  const remove_connection = async (connection: SourceConnection) => {
    const res = await UseModal().showSimple({
      title: "Remove Connection",
      desc: `Are you sure? </br>
      You must first stop any active sessions before deleting a connection.`,
      cancelBtnText: "Cancel",
      submitBtnText: "Remove",
    });
    if (res.type === "success") {
      // remove the connection
      const remove_res = await api.remove_connection({
        source_id,
        connection_id: connection.id,
      });
      if (remove_res.success) {
        connections.value = connections.value.filter(
          (f) => f.id !== connection.id
        );
        UseToast().show({
          type: ToastType.SUCCESS,
          msg: "Connection successfully removed",
          duration: 3000,
        });
      } else {
        UseToast().show({
          type: ToastType.ALERT,
          msg:
            remove_res.code === 423
              ? "<p>Unable to delete connection. <br> There are <b>active sessions</b> associated with this connection.</p>"
              : "Failed to remove connection :(",
          duration: 10000,
        });
      }
    }
  };

  const sessions: Ref<ScanSession[]> = ref([]);
  const add_session = async (connection_id?: string) => {
    const source = get_source();
    if (source.managedExternally && source.id == "onprem_fs") {
      UseModal().show(WalkthroughModalVue, {
        id: "onprem_fs.session",
        height: "50rem",
      });
      return false;
    } else {
      console.log("add_session , ", get_source());
      const res = await UseModal().show(StartSessionModal, {
        source,
        connections: connections.value,
        has_session_params: source.startParams.length > 0,
        sessions: sessions.value,
        connection_id,
      });

      if (res.type === "success") {
        state.all_sources.value = state.all_sources.value.map((s) => {
          if (s.id === source_id) {
            return {
              ...s,
              statistics: {
                ...s.statistics,
                liveRunningScansCount:
                  (s.statistics.liveRunningScansCount || 0) + 1,
              },
            };
          }
          return s;
        });
        init();
        UseToast().show({
          type: ToastType.SUCCESS,
          msg: "Scan successfully started!",
          duration: 3000,
        });
      }
      return res.type === "success";
    }
  };
  const cancel_session = async (session: ScanSession) => {
    const res = await UseModal().showSimple({
      title: "Cancel Scan Session",
      desc:
        "Are you sure?</br>Canceling a session will prevent findings results from being generated.",
      cancelBtnText: "No",
      submitBtnText: "Yes, I'm sure",
    });
    if (res.type === "success") {
      const cancel_session_res = await api.stop_scan_session({
        source_id,
        scan_session_id: session.id,
      });

      if (cancel_session_res.success) {
        sessions.value = sessions.value.map((s) => {
          if (s.id === session.id) {
            return {
              ...s,
              status: "Cancelled",
              reason: "manually stopped",
              endTime: new Date().toISOString(),
            };
          }
          return s;
        });
        state.all_sources.value = state.all_sources.value.map((s) => {
          if (s.id === source_id) {
            return {
              ...s,
              statistics: {
                ...s.statistics,
                liveRunningScansCount:
                  (s.statistics.liveRunningScansCount || 0) - 1,
              },
            };
          }
          return s;
        });
        UseToast().show({
          type: ToastType.SUCCESS,
          msg: "Scan was successfully canceled",
          duration: 3000,
        });
      } else {
        UseToast().show({
          type: ToastType.ALERT,
          msg: "Failed to stop the scan :(",
          duration: 3000,
        });
      }
    }
  };

  const [is_loading_data, set_is_loading_data] = UseLoading(false).as_arr();

  const init = async () => {
    set_is_loading_data(true);
    await delayBy(2000);
    const connections_res = await api.get_source_connections(source_id);
    const sessions_res = await api.get_sessions_for_source(source_id);
    if (connections_res.success && sessions_res.success) {
      connections.value = connections_res.value;
      sessions.value = sessions_res.value;
      connections.value = connections.value.map((c) => {
        const con_sessions = sessions.value.filter(
          (s) => s.connectionId == c.id
        );
        const active_sessions_count = con_sessions.filter(
          (s) => s.status == "Opened"
        ).length;
        // @ts-ignore
        c.sessions = {
          active: active_sessions_count,
          closed: con_sessions.length - active_sessions_count,
        };
        return c;
      });
    }
    set_is_loading_data(false);
  };

  return {
    init,
    is_loading_data,
    get_source,
    is_source_connected: () => {
      const source = get_source();
      console.log("source::", source);
      if (source && source.id) {
        return true;
      }
      return false;
    },
    connections,
    sessions,
    add_session,
    cancel_session,
    add_connection,
    remove_connection,
    refresh_connection_list,
  };
};
